import { z } from 'zod';

import type { Zot } from '@cadstrom/sigma-engine-shared';

const CLICK_ID_KEYS = [
	'gclid',
	'gbraid',
	'wbraid',
	'dclid',
	'fbclid',
	'msclkid',
	'twclid',
	'ttclid',
	'li_fat_id',
	'rdt_cid',
	'epik',
	'sccid',
] as const;

type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

type ClickIdTags = { [K in ClickIdKey]?: string };

const CLICK_ID_SET: ReadonlySet<string> = new Set(CLICK_ID_KEYS);

/**
 * The marketing source of a signup: every `utm_*` tag and ad-platform click ID on the link that began it, plus the
 * external site the visitor came from. Open-ended on `utm_*` so a new tag needs no code change.
 */
export interface CampaignTags extends ClickIdTags {
	[tag: `utm_${string}`]: string | undefined;
	referring_domain?: string;
}

/** Most tags carried per signup. Kept at or below the cap of the Auth0 Action that stores them. */
export const MAX_CAMPAIGN_TAGS = 12;

/** Longest tag value carried. Kept at or below the cap of the Auth0 Action that stores them. */
export const MAX_CAMPAIGN_TAG_VALUE_LENGTH = 120;

// Everything outside the alphabet Auth0 accepts for an `ext-` param value; one stray character fails the whole login.
const DISALLOWED_VALUE_CHARACTERS = /[^-\w.*~@+ /:]/g;

/**
 * Restricts a tag value to the characters Auth0 will carry and caps its length, so one value is identical wherever it
 * travels.
 *
 * @param value - The raw value from a link or referrer.
 * @returns The value with disallowed characters replaced by `_`, trimmed and capped; empty if nothing usable remains.
 */
function sanitizeTagValue(value: string): string {
	return value.replace(DISALLOWED_VALUE_CHARACTERS, '_').trim().slice(0, MAX_CAMPAIGN_TAG_VALUE_LENGTH).trimEnd();
}

/**
 * Whether a search param name is a campaign tag: any lowercase `utm_*` name, or a known ad-platform click ID.
 *
 * @param key - The search param name.
 * @returns True when the param should be carried.
 */
function isCarriedTagKey(key: string): boolean {
	return /^utm_[a-z0-9_]{1,35}$/.test(key) || CLICK_ID_SET.has(key);
}

/**
 * Keeps the entries of a record that are carried tags, with cleaned values.
 *
 * @param source - The record to read.
 * @param isCarried - Whether an entry's name is a tag to keep.
 * @returns The kept entries, at most {@link MAX_CAMPAIGN_TAGS}.
 */
function pickCarriedTags(source: Record<string, unknown>, isCarried: (key: string) => boolean): Record<string, string> {
	const tags: Record<string, string> = {};

	for (const [key, value] of Object.entries(source)) {
		if (Object.keys(tags).length >= MAX_CAMPAIGN_TAGS) {
			break;
		}
		if (!isCarried(key) || typeof value !== 'string') {
			continue;
		}
		const sanitized = sanitizeTagValue(value);
		if (sanitized !== '') {
			tags[key] = sanitized;
		}
	}

	return tags;
}

/**
 * Picks the campaign tags out of a set of search params, ignoring everything else. The referring domain is never read
 * from a link, because a link could claim any referrer.
 *
 * @param search - Search params to read, typically a route's validated search.
 * @returns The carried tags with cleaned values, at most {@link MAX_CAMPAIGN_TAGS}; empty when there were none.
 */
export function extractCampaignTags(search: Record<string, unknown>): CampaignTags {
	return pickCarriedTags(search, isCarriedTagKey);
}

/**
 * The hostname a visitor came from, when it is somewhere other than this app or its login provider.
 *
 * @param referrer - The referrer URL, such as `document.referrer`.
 * @param ownHostnames - Hostnames that do not count as a referral.
 * @returns The cleaned hostname, or undefined when there is no external referrer.
 */
export function externalReferringDomain(referrer: string, ownHostnames: readonly string[]): string | undefined {
	if (referrer === '') {
		return undefined;
	}

	let hostname: string;
	try {
		hostname = new URL(referrer).hostname;
	} catch {
		return undefined;
	}
	if (hostname === '' || ownHostnames.includes(hostname)) {
		return undefined;
	}

	const sanitized = sanitizeTagValue(hostname);
	return sanitized === '' ? undefined : sanitized;
}

/** Where the tags for a login are read from. */
export interface CampaignSource {
	/** The query params of the link that began the login. */
	search: Record<string, unknown>;
	/** The referrer of the page that began the login. */
	referrer: string;
	/** Hostnames that do not count as a referral, such as this app and the login provider. */
	ownHostnames: readonly string[];
}

/**
 * Collects every campaign tag for a login: those on the link plus the external referring domain.
 *
 * @param source - The link and referrer the login began from.
 * @returns The tags, at most {@link MAX_CAMPAIGN_TAGS}; empty when the visit carried none.
 */
export function collectCampaignTags(source: CampaignSource): CampaignTags {
	const tags = extractCampaignTags(source.search);

	const domain = externalReferringDomain(source.referrer, source.ownHostnames);
	if (domain !== undefined && Object.keys(tags).length < MAX_CAMPAIGN_TAGS) {
		tags.referring_domain = domain;
	}

	return tags;
}

/**
 * Names the tags as person properties, so the source of a signup stays with the user after the event.
 *
 * @param tags - The campaign tags of the signup.
 * @returns Each tag under a `signup_` prefix, which keeps it apart from PostHog's own first-visit `$initial_*` values.
 */
export function toSignupPersonProperties(tags: CampaignTags): Record<string, string> {
	const properties: Record<string, string> = {};

	for (const [key, value] of Object.entries(tags)) {
		if (typeof value === 'string') {
			properties[`signup_${key}`] = value;
		}
	}

	return properties;
}

/** Tags as stored in `appState` or the ID token claim, which may include the referring domain. */
export const campaignTagsSchema = z
	.record(z.string(), z.string())
	.transform((tags) =>
		pickCarriedTags(tags, (key) => isCarriedTagKey(key) || key === 'referring_domain'),
	) satisfies Zot<CampaignTags>;
